import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowRightLeft, Plus, RefreshCw } from 'lucide-react';
import { db } from '../db';
import axios from 'axios';
import { toast } from 'sonner';
import { hapticImpact, hapticNotification, isNative } from '../native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Common travel currencies
const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', flag: '🇹🇭' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', flag: '🇻🇳' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', flag: '🇲🇽' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
  { code: 'AUD', name: 'Australian Dollar', symbol: '$', flag: '🇦🇺' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: '$', flag: '🇨🇦' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', flag: '🇨🇭' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: '$', flag: '🇸🇬' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: '$', flag: '🇳🇿' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', flag: '🇮🇩' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', flag: '🇵🇭' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', flag: '🇲🇾' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦' },
];

// Client-side cache for all exchange rates (persists in memory during session)
let cachedRates = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const QuickConverter = ({ onAddExpense, baseCurrency = 'USD' }) => {
  const [amount, setAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('THB');
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [allRates, setAllRates] = useState(null);

  // Load all rates ONCE on component mount (ONE API CALL per 24 hours)
  const loadAllRates = useCallback(async (forceRefresh = false) => {
    // Check in-memory cache first (instant, no API call)
    if (!forceRefresh && cachedRates && cacheTimestamp) {
      const age = Date.now() - cacheTimestamp;
      if (age < CACHE_DURATION_MS) {
        setAllRates(cachedRates);
        setLastUpdated(new Date(cacheTimestamp));
        return cachedRates;
      }
    }

    // Check IndexedDB cache (persists across app restarts)
    if (!forceRefresh) {
      try {
        const stored = await db.settings.get('bulk_exchange_rates');
        if (stored?.value) {
          const parsed = JSON.parse(stored.value);
          const storedTime = new Date(parsed.last_updated).getTime();
          const age = Date.now() - storedTime;
          
          if (age < CACHE_DURATION_MS) {
            cachedRates = parsed.rates;
            cacheTimestamp = storedTime;
            setAllRates(parsed.rates);
            setLastUpdated(new Date(storedTime));
            console.log('Using cached rates from IndexedDB (no API call)');
            return parsed.rates;
          }
        }
      } catch (e) {
        console.log('No cached rates in IndexedDB');
      }
    }

    // Fetch from server - ONE API call for ALL currencies
    try {
      setIsLoading(true);
      console.log('Fetching bulk exchange rates (1 API call for all currencies)');
      const response = await axios.get(`${API}/exchange-rates`);
      const rates = response.data.rates;
      const timestamp = new Date(response.data.last_updated);
      
      // Store in memory (instant access)
      cachedRates = rates;
      cacheTimestamp = timestamp.getTime();
      
      // Store in IndexedDB (persists across sessions)
      await db.settings.put({
        key: 'bulk_exchange_rates',
        value: JSON.stringify({
          rates: rates,
          last_updated: timestamp.toISOString()
        })
      });
      
      setAllRates(rates);
      setLastUpdated(timestamp);
      return rates;
    } catch (error) {
      console.error('Failed to fetch rates:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load rates and last currency on mount
  useEffect(() => {
    loadAllRates();
    
    const loadLastCurrency = async () => {
      try {
        const lastCurrency = await db.settings.get('last_converter_currency');
        if (lastCurrency?.value) {
          setFromCurrency(lastCurrency.value);
        }
      } catch (error) {
        console.error('Failed to load last currency:', error);
      }
    };
    loadLastCurrency();
  }, [loadAllRates]);

  // Convert when amount or currency changes - NO API CALLS, uses cached rates
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && allRates) {
      convertCurrency();
    } else {
      setConvertedAmount(null);
      setExchangeRate(null);
    }
  }, [amount, fromCurrency, allRates]);

  const convertCurrency = () => {
    if (!amount || parseFloat(amount) <= 0 || !allRates) return;

    // Same currency = no conversion
    if (fromCurrency === baseCurrency) {
      setConvertedAmount(parseFloat(amount));
      setExchangeRate(1);
      return;
    }

    // Use cached rates - ZERO API calls
    const fromRate = allRates[fromCurrency] || 1;
    const toRate = allRates[baseCurrency] || 1;

    // Convert: amount in fromCurrency -> USD -> baseCurrency
    const converted = (parseFloat(amount) / fromRate) * toRate;
    const rate = toRate / fromRate;

    setConvertedAmount(converted);
    setExchangeRate(rate);
  };

  const handleCurrencyChange = async (currency) => {
    if (isNative) hapticImpact('light');
    setFromCurrency(currency);
    await db.settings.put({ key: 'last_converter_currency', value: currency });
  };

  const handleRefreshRates = async () => {
    if (isNative) hapticImpact('medium');
    toast.info('Refreshing exchange rates...');
    await loadAllRates(true); // Force refresh
    toast.success('Rates updated!');
  };

  const handleAddAsExpense = () => {
    if (!amount || !convertedAmount) return;
    
    if (isNative) hapticNotification('success');
    
    onAddExpense({
      amount: parseFloat(amount),
      currency: fromCurrency,
      convertedAmount: convertedAmount,
      baseCurrency: baseCurrency
    });
    
    setAmount('');
    setConvertedAmount(null);
    toast.success('Opening expense form...');
  };

  const getCurrencySymbol = (code) => {
    const currency = CURRENCIES.find(c => c.code === code);
    return currency?.symbol || code;
  };

  const getCurrencyFlag = (code) => {
    const currency = CURRENCIES.find(c => c.code === code);
    return currency?.flag || '💱';
  };

  return (
    <Card data-testid="quick-converter-card" className="bg-gradient-to-br from-primary/10 to-card border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-primary" />
          Quick Price Check
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefreshRates}
            className="ml-auto h-8 w-8 p-0"
            title="Refresh rates"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Convert prices instantly • Add as expense if needed
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Amount Input */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              data-testid="converter-amount-input"
              type="number"
              inputMode="decimal"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="text-lg font-semibold h-12"
            />
          </div>
          <Select value={fromCurrency} onValueChange={handleCurrencyChange}>
            <SelectTrigger data-testid="converter-currency-select" className="w-28 h-12">
              <SelectValue>
                <span className="flex items-center gap-1">
                  <span>{getCurrencyFlag(fromCurrency)}</span>
                  <span>{fromCurrency}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {CURRENCIES.map((currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  <span className="flex items-center gap-2">
                    <span>{currency.flag}</span>
                    <span>{currency.code}</span>
                    <span className="text-muted-foreground text-xs">- {currency.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Conversion Result */}
        {convertedAmount !== null && (
          <div 
            data-testid="conversion-result"
            className="bg-background/50 rounded-xl p-4 border border-primary/20 animate-in"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Converts to</p>
                <p className="text-2xl font-bold text-primary">
                  {getCurrencySymbol(baseCurrency)}{convertedAmount.toFixed(2)}
                  <span className="text-sm font-normal text-muted-foreground ml-1">{baseCurrency}</span>
                </p>
              </div>
            </div>
            
            {/* Exchange rate info */}
            {exchangeRate && (
              <p className="text-xs text-muted-foreground mt-2">
                1 {fromCurrency} = {exchangeRate.toFixed(4)} {baseCurrency}
                {lastUpdated && (
                  <span className="ml-2 opacity-70">
                    • Rates from {lastUpdated.toLocaleDateString()}
                  </span>
                )}
              </p>
            )}
            
            {/* Add as Expense Button */}
            <Button
              data-testid="add-converted-expense-button"
              onClick={handleAddAsExpense}
              className="w-full mt-3 h-11 bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add as Expense ({getCurrencySymbol(baseCurrency)}{convertedAmount.toFixed(2)})
            </Button>
          </div>
        )}

        {/* Empty state hint */}
        {!amount && (
          <p className="text-center text-sm text-muted-foreground py-2">
            Enter an amount to see instant conversion
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickConverter;
